import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { AdminUserListDto } from '../types/admin-user.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

const schema = z.object({
  email: z.email('Ingresa un correo electrónico válido.').max(254),
});

type FormValues = z.infer<typeof schema>;

interface ResendInvitationModalProps {
  user: AdminUserListDto | null;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (email: string) => void;
}

export function ResendInvitationModal({
  user,
  isPending,
  onClose,
  onConfirm,
}: ResendInvitationModalProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
    if (user) {
      form.reset({ email: user.email });
    }
  }, [user, form]);

  const onSubmit = (values: FormValues) => {
    onConfirm(values.email);
  };

  const handleClose = () => {
    if (!isPending) {
      form.reset();
      onClose();
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle>Reenviar invitación</DialogTitle>
          <DialogDescription>
            Se enviará un nuevo enlace de configuración de contraseña (válido 24 h). Puedes cambiar
            el correo destino.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Correo electrónico</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      disabled={isPending}
                      {...field}
                      data-testid="admin-users-resend-email-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <UnifiedButton
                type="button"
                buttonType="basic"
                variant="default"
                onClick={handleClose}
                disabled={isPending}
                data-testid="admin-users-resend-cancel-button"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                buttonType="basic"
                variant="outline"
                disabled={isPending}
                actionType={'submit'}
                data-testid="admin-users-resend-submit-button"
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar invitación'
                )}
              </UnifiedButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
